// THE APP-WIDE CRASH SCREEN.
//
// Before this existed a render error white-paged the whole SPA (2026-08-18, ESGWND, during
// Sarah chat testing) and left no trace anywhere. This boundary does two jobs: give the user
// a way back (retry re-renders in place; reload is the reliable escape hatch), and file the
// error to `clientErrors` via lib/clientErrors.js so the crash is diagnosable after the fact.
//
// Mounted twice on purpose:
//  - main.jsx, outermost — the safety net. Catches crashes in AuthProvider/router/login that
//    the inner one can't see. No context available here, so it uses no hooks/context.
//  - App.jsx AppProviders — the working boundary. Catches the common case (a page or provider
//    below auth crashing) without unmounting the auth session, so "Try again" can succeed.
import { Component } from 'react'
import { reportClientError } from '../lib/clientErrors'

export default class ErrorBoundary extends Component {
  state = { error: null, detailOpen: false }

  static getDerivedStateFromError(error) { return { error } }

  componentDidCatch(error, info) {
    reportClientError(error, { componentStack: info?.componentStack || null, source: 'boundary' })
  }

  render() {
    if (!this.state.error) return this.props.children
    const message = String(this.state.error?.message || this.state.error || 'Unknown error')
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
          <div className="text-4xl mb-3" aria-hidden="true">⚠️</div>
          <h1 className="text-lg font-semibold text-slate-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-slate-600 mb-6">
            This part of InCite hit an unexpected error. It's been reported automatically —
            your data is safe. Try again, or reload the page to pick up where you left off.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => this.setState({ error: null, detailOpen: false })}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
            >
              Reload page
            </button>
          </div>
          <button
            onClick={() => this.setState((s) => ({ detailOpen: !s.detailOpen }))}
            className="mt-5 text-[11px] text-slate-400 hover:text-slate-600 underline"
          >
            {this.state.detailOpen ? 'Hide' : 'Show'} technical details
          </button>
          {this.state.detailOpen && (
            <pre className="mt-2 text-left text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap">
              {message}
            </pre>
          )}
        </div>
      </div>
    )
  }
}
